import type { Channel } from '@/types/channel';

/**
 * Display form of an Instagram username: exactly one leading `@`. Some stored
 * handles already carry an `@` (older connect flows persisted it verbatim), so
 * a blind `@${username}` renders `@@handle` (AIT-52). Prepend only when missing.
 */
export function formatInstagramHandle(username: string): string {
  return `@${username.replace(/^@+/, '')}`;
}

/**
 * Channel-type-aware display name used in sidebars, titles, and dialogs.
 * Instagram prefers the human-readable display name (`instagramProfileName`)
 * when Meta returns one distinct from the handle; falls back to `@username`
 * then a generic 'Instagram Channel'. WhatsApp shows the WABA/business name
 * with the display phone appended (`<name> · <phone>`) so two numbers sharing
 * one WABA are distinguishable; falls back to name-only, then 'WhatsApp
 * Channel', when the phone is absent.
 */
export function channelDisplayName(channel: Channel): string {
  if (channel.type === 'instagram') {
    const trimmedName = channel.instagramProfileName?.trim();
    if (trimmedName) return trimmedName;
    return channel.instagramUsername
      ? formatInstagramHandle(channel.instagramUsername)
      : 'Instagram Channel';
  }
  if (channel.type === 'whatsapp') {
    // Two numbers can share one WABA (multiple phone numbers per WABA), so the
    // WABA/business name alone is ambiguous. Append the display phone number so
    // sibling numbers are always distinguishable in lists, titles, and
    // destructive-confirmation dialogs (multi-number-per-waba design, D9).
    const base = channel.whatsappWabaName?.trim() || 'WhatsApp Channel';
    // Meta returns the display phone pretty-formatted (e.g. "+972 55-704-6276").
    // Strip the "+", dashes, and spaces so the sidebar shows one bare digit run.
    const phone = channel.whatsappDisplayPhoneNumber?.replace(/[+\s-]/g, '');
    return phone ? `${base} · ${phone}` : base;
  }
  // facebook (AIT-621): the Page name; a neutral label when Meta gave none.
  return channel.facebookPageName?.trim() || 'Facebook Page';
}

/**
 * Human-readable noun phrase for the channel kind — used in user-facing copy
 * like "Disconnect your {noun}". Distinct from `channelDisplayName` because
 * a generic noun is right for confirmation prose where the operator already
 * knows which specific account they're disconnecting from the dialog title.
 */
export function channelTypeNoun(channel: Channel): string {
  if (channel.type === 'instagram') return 'Instagram account';
  if (channel.type === 'facebook') return 'Facebook Page';
  return 'WhatsApp channel';
}
